import ReactMarkdown from "react-markdown";
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import { TimeReference } from "./remark_directives/TimeReference";
import { YTVideoEmbed } from "./remark_directives/YTVideoEmbed";
import { TimelineSectionBar } from "./remark_directives/TimelineSectionBar";

// Array of valid component names
const validComponents = ["TimeReference", "VideoEmbed", "TimelineSectionBar"];

// Remark plugin to transform custom directives into elements with props
function remarkCustomDirective() {
  // Function to convert attribute names to camelCase
  const toCamelCase = (str) =>
    str.replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace("-", "").replace("_", "")
    );

  return (tree) => {
    visit(
      tree,
      ["textDirective", "leafDirective", "containerDirective"],
      (node, index, parent) => {
        if (validComponents.includes(node.name)) {
          const data = node.data || (node.data = {});
          // Use lowercase tag name for HTML tag
          data.hName = node.name.toLowerCase();

          const attributes = node.attributes || {};
          const hProperties = {};
          for (const [key, value] of Object.entries(attributes)) {
            // Convert attribute names to camelCase
            const propName = toCamelCase(key);
            hProperties[propName] = value;
          }

          // If the directive is inline, set an 'inline' prop
          if (node.type === "textDirective") {
            hProperties.as = "span";
          }

          // Generate a stable React key based on directive attributes so it remains
          // unchanged even if the markdown content above this node grows during
          // token-by-token streaming. Falling back to the original line/column-based
          // key only when no attributes are present.
          const attrKeyPart = Object.entries(attributes)
            .sort(([a], [b]) => a.localeCompare(b)) // ensure deterministic order
            .map(([k, v]) => `${k}-${v}`)
            .join("-");

          if (attrKeyPart) {
            hProperties._key = `${node.name}-${attrKeyPart}`;
          } else if (node.position?.start) {
            // Fallback for edge-cases where there are genuinely no attributes
            hProperties._key = `${node.name}-${node.position.start.line}-${node.position.start.column}`;
          } else {
            hProperties._key = `${node.name}-fallback`;
          }

          data.hProperties = hProperties;
        } else {
          // Remove unrecognized directives from the AST
          parent.children.splice(index, 1);
          // Decrement the index to account for the removed node
          return index - 1;
        }
      }
    );
  };
}

// Utility to fix malformed directive lines where the final attribute is a time string
// (H:MM:SS, HH:MM:SS, or MM:SS) and the developer forgot to close the final quote
// and/or the closing `}`. This attempts a best-effort repair so that `remark-directive`
// can still successfully parse the directive.
function sanitizeUnclosedDirective(markdown: string): string {
  return markdown
    .split(/\n/)
    .map((line) => {
      // Quickly skip anything that clearly is not a directive line
      if (!/:\w+\{/.test(line)) return line;

      const hasOpenBrace = line.includes("{");
      const hasCloseBrace = line.includes("}");
      const unclosedBrace = hasOpenBrace && !hasCloseBrace;

      const quoteCount = (line.match(/\"/g) || []).length;
      const unmatchedQuote = quoteCount % 2 === 1;

      // We only want to apply the fix when the *last* argument value looks like a time
      const timeLike = /(\d{1,2}:\d{2}(?::\d{2})?)\s*$/.test(line);

      if (timeLike && (unclosedBrace || unmatchedQuote)) {
        let fixed = line;
        if (unmatchedQuote) fixed += '"';
        if (unclosedBrace) fixed += '}';
        return fixed;
      }

      return line;
    })
    .join("\n");
}

const components = {
  timereference: ({ node, _key, ...rest }) => (
    <TimeReference key={_key} {...rest} />
  ),
  videoembed: ({ node, _key, ...rest }) => (
    <YTVideoEmbed key={_key} {...rest} />
  ),
  timelinesectionbar: ({ node, _key, ...rest }) => (
    <TimelineSectionBar key={_key} {...rest} />
  ),
};

export function CustomMarkdownRenderer(props: { rawMarkdownContent: string }) {
  const targetText = sanitizeUnclosedDirective(props.rawMarkdownContent);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkDirective, remarkCustomDirective]}
      components={components}
    >
      {targetText}
    </ReactMarkdown>
  );
}
